import { useCallback, useState } from 'react';
import api from '@/services/api';

interface ConversationHistoryParams {
  start: string;
  end: string;
}

interface ConversationHistoryResponse {
  threadId: string;
  user: string;
  start: string;
  messages: Array<{
    type: string;
    content: string;
    start: string;
    feedback?: string;
  }>;
  usage: {
    model: string;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    inputCacheTokens: number;
    outputReasoningTokens: number;
  };
  traces: string[];
}

export const useConversationHistory = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ConversationHistoryResponse[] | null>(null);

  const getConversationHistory = useCallback(async (params: ConversationHistoryParams) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/v1/reports/conversation_history`, {
        withCredentials: true,
        params,
      });

      setData(response.data);
      return response.data;
    } catch (err: any) {
      let errorMessage = 'Error getting conversation history';

      if (err.response?.status === 403) {
        errorMessage =
          'You are not authorized to access the conversation history. Contact the administrator.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      console.error('Error fetching conversation history:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearData = useCallback(() => {
    setData(null);
  }, []);

  return {
    getConversationHistory,
    loading,
    error,
    data,
    clearError,
    clearData,
  };
};
